import { webhookConfig } from '@your-org/make-webhook-client';
import { TeamPerformanceData } from '../contexts/AppContext';
import { authService } from './authService.ts';
import { formatPhoneNumber } from '../utils/validationUtils.ts';
import { getTeams } from './firebase/teamFirebaseService.ts';

// 팀별 전화번호 정보
export interface TeamPhoneNumbers {
  displayName: string;
  phoneNumber: string;
  email: string;
}

// 팀 성과 웹훅 데이터 타입 정의
export interface TeamPerformanceWebhookData {
  event: 'daily_report_uploaded';
  data: {
    uploadDate: string;
    reportDate: string;
    uploadedBy: string;
    fileName: string;
    totalTeams: number;
    totalTargetSales: number;
    totalActualSales: number;
    overallAchievementRate: number;
    teams: TeamPerformanceWebhookTeamData[];
    summary: {
      exceededTeams: number;
      underperformingTeams: number;
      totalExcessAmount: number;
      totalShortfallAmount: number;
    };
  };
}

export interface TeamPerformanceWebhookTeamData {
  teamName: string;
  targetSales: number;
  actualSales: number;
  achievementRate: number;
  excessAmount: number;
  shortfallAmount: number;
  status: 'exceeded' | 'achieved' | 'underperforming';
  incentiveAmount: number;
  exceededSales: number;
  underperformanceSales: number;
  deductionAmount: number;
  incentiveCalculationSales: number;
  // 팀별 전화번호 정보 추가
  phoneNumbers: TeamPhoneNumbers[];
}

// 일계표 웹훅 서비스 클래스
export class DailyReportWebhookService {
  private static instance: DailyReportWebhookService;

  private constructor() {}

  public static getInstance(): DailyReportWebhookService {
    if (!DailyReportWebhookService.instance) {
      DailyReportWebhookService.instance = new DailyReportWebhookService();
    }
    return DailyReportWebhookService.instance;
  }

  // 팀별 전화번호 정보 가져오기
  private async getTeamPhoneNumbers(teamName: string): Promise<TeamPhoneNumbers[]> {
    try {
      console.log(`[DailyReportWebhook] 🔍 Getting phone numbers for team: "${teamName}"`);
      
      // 1. Firebase에서 팀 정보 가져오기
      console.log(`[DailyReportWebhook] 🔍 Fetching teams from Firebase...`);
      const teams = await getTeams() as any[];
      console.log(`[DailyReportWebhook] 📊 Total teams loaded: ${teams.length}`, teams);
      
      // 2. 팀 이름으로 팀 ID 찾기
      const targetTeam = teams.find((team: any) => team.name === teamName);
      if (!targetTeam) {
        console.log(`[DailyReportWebhook] ❌ Team "${teamName}" not found in Firebase teams collection`);
        return [];
      }
      
      const teamId = targetTeam.id;
      console.log(`[DailyReportWebhook] ✅ Found team ID for "${teamName}": ${teamId}`);
      
      // 3. 모든 사용자 가져오기
      const allUsers = await authService.getAllUsers();
      console.log(`[DailyReportWebhook] 📊 Total users loaded: ${allUsers.length}`);
      
      // 모든 사용자의 팀 정보 표시
      console.log(`[DailyReportWebhook] 👥 All users team info:`, 
        allUsers.map(user => ({
          email: user.email,
          displayName: user.displayName,
          teamId: user.teamId,
          hasPhoneNumber: !!user.phoneNumber
        }))
      );
      
      // 4. 팀 ID로 사용자 필터링
      console.log(`[DailyReportWebhook] 🎯 Filtering users by teamId: "${teamId}"`);
      
      const teamMembers = allUsers.filter(user => {
        if (!user.teamId) {
          console.log(`[DailyReportWebhook] ❌ User "${user.displayName}" has no teamId`);
          return false;
        }
        const isMatch = user.teamId === teamId;
        
        console.log(`[DailyReportWebhook] 🔍 User "${user.displayName}": teamId="${user.teamId}" vs target="${teamId}" → ${isMatch ? '✅' : '❌'}`);
        
        return isMatch;
      });

      console.log(`[DailyReportWebhook] Found ${teamMembers.length} members for team ${teamName} (teamId: ${teamId})`);
      
      // 전화번호 정보 구성 (전화번호가 있는 멤버만)
      const phoneNumbers: TeamPhoneNumbers[] = teamMembers
        .filter(member => {
          const hasPhone = !!member.phoneNumber;
          console.log(`[DailyReportWebhook] 📞 Member "${member.displayName}": hasPhoneNumber=${hasPhone}, phoneNumber="${member.phoneNumber}"`);
          return hasPhone;
        })
        .map(member => ({
          displayName: member.displayName || member.email,
          phoneNumber: formatPhoneNumber(member.phoneNumber!), // UI 표시용 포맷팅
          email: member.email
        }));

      console.log(`[DailyReportWebhook] Found ${phoneNumbers.length} phone numbers for team ${teamName}`);
      console.log(`[DailyReportWebhook] 📋 Phone numbers result:`, phoneNumbers);
      
      return phoneNumbers;
      
    } catch (error) {
      console.error(`[DailyReportWebhook] Error getting phone numbers for team ${teamName}:`, error);
      return []; // 오류 시 빈 배열 반환
    }
  }

  // 팀 성과 데이터를 웹훅 형식으로 변환
  private async transformTeamData(teamData: TeamPerformanceData): Promise<TeamPerformanceWebhookTeamData> {
    const achievementRate = teamData.targetSales > 0 ? (teamData.actualSales / teamData.targetSales) * 100 : 0;
    const excessAmount = Math.max(0, teamData.actualSales - teamData.targetSales);
    const shortfallAmount = Math.max(0, teamData.targetSales - teamData.actualSales);
    
    let status: 'exceeded' | 'achieved' | 'underperforming';
    if (achievementRate >= 100) {
      status = 'exceeded';
    } else if (achievementRate >= 90) {
      status = 'achieved';
    } else {
      status = 'underperforming';
    }

    // 팀별 전화번호 정보 가져오기
    const phoneNumbers = await this.getTeamPhoneNumbers(teamData.teamName);

    return {
      teamName: teamData.teamName,
      targetSales: teamData.targetSales,
      actualSales: teamData.actualSales,
      achievementRate: Math.round(achievementRate * 100) / 100,
      excessAmount: Math.round(excessAmount),
      shortfallAmount: Math.round(shortfallAmount),
      status,
      incentiveAmount: teamData.incentiveAmount,
      exceededSales: teamData.exceededSales,
      underperformanceSales: teamData.underperformanceSales,
      deductionAmount: teamData.deductionAmount,
      incentiveCalculationSales: teamData.incentiveCalculationSales,
      phoneNumbers: phoneNumbers
    };
  }

  // 웹훅 데이터 생성
  private async createWebhookData(
    teamsData: TeamPerformanceData[],
    uploadInfo: {
      uploadDate: string;
      reportDate: string;
      uploadedBy: string;
      fileName: string;
    }
  ): Promise<TeamPerformanceWebhookData> {
    // 모든 팀 데이터를 병렬로 변환 (전화번호 조회 포함)
    const transformedTeams = await Promise.all(
      teamsData.map(team => this.transformTeamData(team))
    );
    
    const totalTargetSales = transformedTeams.reduce((sum, team) => sum + team.targetSales, 0);
    const totalActualSales = transformedTeams.reduce((sum, team) => sum + team.actualSales, 0);
    const overallAchievementRate = totalTargetSales > 0 ? (totalActualSales / totalTargetSales) * 100 : 0;
    
    const exceededTeams = transformedTeams.filter(team => team.status === 'exceeded').length;
    const underperformingTeams = transformedTeams.filter(team => team.status === 'underperforming').length;
    const totalExcessAmount = transformedTeams.reduce((sum, team) => sum + team.excessAmount, 0);
    const totalShortfallAmount = transformedTeams.reduce((sum, team) => sum + team.shortfallAmount, 0);

    return {
      event: 'daily_report_uploaded',
      data: {
        ...uploadInfo,
        totalTeams: transformedTeams.length,
        totalTargetSales: Math.round(totalTargetSales),
        totalActualSales: Math.round(totalActualSales),
        overallAchievementRate: Math.round(overallAchievementRate * 100) / 100,
        teams: transformedTeams,
        summary: {
          exceededTeams,
          underperformingTeams,
          totalExcessAmount: Math.round(totalExcessAmount),
          totalShortfallAmount: Math.round(totalShortfallAmount)
        }
      }
    };
  }

  // 웹훅 전송
  public async sendTeamPerformanceWebhook(
    teamsData: TeamPerformanceData[],
    uploadInfo: {
      uploadDate: string;
      reportDate: string;
      uploadedBy: string;
      fileName: string;
    }
  ): Promise<{ success: boolean; message: string; response?: any }> {
    try {
      // 웹훅 URL 확인
      const webhookUrl = webhookConfig.getWebhookUrl('analytics');
      if (!webhookUrl) {
        return {
          success: false,
          message: '웹훅 URL이 설정되지 않았습니다. 설정에서 웹훅 URL을 먼저 설정해주세요.'
        };
      }

      // 웹훅 데이터 생성 (팀별 전화번호 정보 포함)
      const webhookData = await this.createWebhookData(teamsData, uploadInfo);

      // 웹훅 전송
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // 응답이 JSON인지 확인
      const contentType = response.headers.get('content-type');
      let responseData;
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // JSON이 아닌 경우 텍스트로 처리
        const textData = await response.text();
        responseData = { message: textData };
      }

      console.log('팀 성과 웹훅 전송 성공:', webhookData);
      
      return {
        success: true,
        message: '팀 성과 데이터가 성공적으로 전송되었습니다.',
        response: responseData
      };

    } catch (error) {
      console.error('팀 성과 웹훅 전송 실패:', error);
      
      return {
        success: false,
        message: `웹훅 전송 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`
      };
    }
  }

  // 웹훅 설정 확인
  public checkWebhookConfig(): { isValid: boolean; message: string } {
    const settings = webhookConfig.getSettings();
    
    if (!settings.analyticsWebhookUrl) {
      return {
        isValid: false,
        message: '분석 웹훅 URL이 설정되지 않았습니다.'
      };
    }

    return {
      isValid: true,
      message: '웹훅 설정이 완료되었습니다.'
    };
  }
}

// 싱글톤 인스턴스 export
export const dailyReportWebhookService = DailyReportWebhookService.getInstance(); 